/**
 * Criteria Selection Task Component
 * 
 * Allows a participant to select which attributes should be used as criteria
 * for prioritization. Selected criteria will be used in weighted scoring
 * or multi-criteria analysis.
 */

import { useState, useMemo, useEffect } from 'react';
import {
  Save,
  CheckCircle,
  AlertCircle,
  ListChecks,
  Check,
  Loader2,
} from 'lucide-react';
import type {
  PrioritizationTask,
  CriteriaSelectionResponse,
} from '../../../types/prioritization/prioritizationTask';
import { usePrioritizationTaskStore } from '../../../stores/prioritization/prioritizationTaskStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { isObjectiveAttribute } from '../../../lib/objectiveCriteria';

// ============================================================================
// Types
// ============================================================================

export interface CriteriaSelectionTaskProps {
  task: PrioritizationTask;
  userId: string;
  userName: string;
  onComplete?: () => void;
}

// ============================================================================
// Main Component
// ============================================================================

export function CriteriaSelectionTask({ task, userId, userName, onComplete }: CriteriaSelectionTaskProps) {
  // State
  const [selectedCriteria, setSelectedCriteria] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get attributes from store with sync state
  const allAttributes = useAttributesStore(state => state.attributes);
  const syncState = useAttributesStore(state => state.syncState);
  const fetchFromSupabase = useAttributesStore(state => state.fetchFromSupabase);
  
  // Ensure attributes are loaded from Supabase on mount
  useEffect(() => {
    // Only fetch if we haven't synced recently (avoid redundant fetches)
    if (!syncState.lastSyncAt || (Date.now() - syncState.lastSyncAt.getTime() > 60000)) {
      fetchFromSupabase();
    }
  }, [fetchFromSupabase, syncState.lastSyncAt]);
  const availableAttributes = useMemo(() => {
    // Custom fields are excluded unless explicitly opted in via usable_as_criterion.
    // System attributes are always eligible.
    // A strategic objective (1066) is chosen on a track or in the cycle, three at most.
    return allAttributes.filter(a =>
      (!a.isCustomField || a.usable_as_criterion === true) && !isObjectiveAttribute(a)
    );
  }, [allAttributes]);
  
  // Store actions
  const submitResponse = usePrioritizationTaskStore(state => state.submitResponse);
  
  // Check if already submitted
  const existingResponse = task.responses.find(r => r.participantId === userId);
  
  // Show loading state while attributes are being fetched
  if (syncState.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-[var(--text-ter)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500 mb-4" />
        <p>Loading available criteria...</p>
      </div>
    );
  }
  
  const toggleCriteria = (attributeId: string) => {
    setSelectedCriteria(prev => {
      const next = new Set(prev);
      if (next.has(attributeId)) {
        next.delete(attributeId);
      } else {
        next.add(attributeId);
      }
      return next;
    });
  };
  
  const handleSubmit = async () => {
    if (selectedCriteria.size === 0) return;
    
    setIsSubmitting(true);
    
    try {
      const responseData: CriteriaSelectionResponse = {
        type: 'criteria-selection',
        selectedCriteriaIds: Array.from(selectedCriteria),
      };
      
      submitResponse({
        taskId: task.id,
        participantId: userId,
        participantName: userName,
        data: responseData,
      });
      
      onComplete?.();
    } catch (error) {
      console.error('[CriteriaSelectionTask] Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Show existing response if already submitted
  if (existingResponse) {
    const data = existingResponse.data as CriteriaSelectionResponse;
    return (
      <div className="space-y-6">
        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-4">
          <div className="flex items-center gap-2 text-[var(--green)] dark:text-[var(--text-ter)]">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Criteria selected</span>
          </div>
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
            You selected {data.selectedCriteriaIds.length} criteria on {new Date(existingResponse.submittedAt).toLocaleString()}.
          </p>
        </div>
        
        <div className="bg-[var(--bg-raised)] border p-4">
          <h3 className="font-medium text-[var(--text-pri)] mb-3">Your Selected Criteria</h3>
          <div className="flex flex-wrap gap-2">
            {data.selectedCriteriaIds.map(key => {
              const attr = allAttributes.find(a => a.key === key);
              return (
                <span key={key} className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm">
                  {attr?.label || key}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <ListChecks className="h-12 w-12 mx-auto text-[var(--mustard)] mb-4" />
        <h2 className="text-xl font-semibold text-[var(--text-pri)]">
          Select Prioritization Criteria
        </h2>
        <p className="text-[var(--text-sec)] mt-2">
          Choose which attributes should be used to evaluate and compare items.
          Select at least 2 criteria for effective prioritization.
        </p>
      </div>
      
      {/* Selection Count */}
      <div className="flex items-center justify-center gap-4">
        <div className={`px-4 py-2  ${
          selectedCriteria.size >= 2 
            ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' 
            : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
        }`}>
          <span className="font-medium">{selectedCriteria.size}</span> criteria selected
        </div>
      </div>
      
      {/* Criteria List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {availableAttributes.length > 0 ? (
          availableAttributes.map(attr => {
            const isSelected = selectedCriteria.has(attr.key);
            return (
              <button
                key={attr.key}
                onClick={() => toggleCriteria(attr.key)}
                className={`p-4  border-2 text-left transition-all ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-[var(--text-pri)]">{attr.label}</div>
                    {attr.validation?.pattern && (
                      <div className="text-sm text-[var(--text-ter)] mt-1">Pattern: {attr.validation.pattern}</div>
                    )}
                    <div className="text-xs text-[var(--text-ter)] mt-2">
                      Type: {attr.type} | Key: {attr.key}
                    </div>
                  </div>
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    isSelected
                      ? 'border-primary-500 bg-primary-500 text-white'
                      : 'border-[var(--rule)]'
                  }`}>
                    {isSelected && <Check className="h-4 w-4" />}
                  </div>
                </div>
              </button>
            );
          })
        ) : (
          <div className="col-span-2 text-center py-8 text-[var(--text-ter)]">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
            <p>No attributes available for selection.</p>
            <p className="text-sm">Please configure attributes in Settings first, or contact your administrator.</p>
          </div>
        )}
      </div>
      
      {/* Common Criteria Suggestions */}
      {availableAttributes.length === 0 && (
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
          <h3 className="font-medium text-[var(--text-sec)] mb-2">Suggested Criteria</h3>
          <div className="grid grid-cols-2 gap-2 text-sm text-[var(--text-sec)]">
            <div>• Business Value</div>
            <div>• Effort/Cost</div>
            <div>• Strategic Alignment</div>
            <div>• Risk Level</div>
            <div>• Time Sensitivity</div>
            <div>• Dependencies</div>
          </div>
        </div>
      )}
      
      {/* Submit */}
      <div className="flex items-center justify-end">
        <button
          onClick={handleSubmit}
          disabled={selectedCriteria.size < 1 || isSubmitting}
          className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            'Submitting...'
          ) : (
            <>
              <Save className="h-4 w-4" />
              Submit Selection
            </>
          )}
        </button>
      </div>
      
      {selectedCriteria.size < 2 && selectedCriteria.size > 0 && (
        <div className="flex items-center gap-2 text-[var(--mustard)] text-sm">
          <AlertCircle className="h-4 w-4" />
          Consider selecting at least 2 criteria for balanced prioritization
        </div>
      )}
    </div>
  );
}

export default CriteriaSelectionTask;
